import React, { useEffect, useRef, useState } from 'react';
import { ArrowRightLeft, Check, RefreshCw } from 'lucide-react';
import axios from '../axiosInstance';
import './CustomerSharing.css';

const message = e => e.response?.data?.message || 'Sharing could not be saved. Try again.';

export default function PipelineSharingSettings({ pipelineId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  const [revision, refresh] = useState(0);
  const [dirty, setDirty] = useState(false);
  const active = useRef(false);
  useEffect(() => {
    let current = true;
    active.current = true;
    setData(null); setError(''); setDirty(false); setSummary(null);
    axios.get(`/customer-sharing/pipelines/${pipelineId}`).then(({ data: response }) => {
      if (current) setData(response.data);
    }).catch(e => { if (current) setError(message(e)); });
    return () => { current = false; active.current = false; };
  }, [pipelineId, revision]);
  const change = values => { setData(previous => ({ ...previous, ...values })); setDirty(true); };
  const save = async () => {
    setBusy(true); setError(''); setSummary(null);
    try {
      const response = await axios.put(`/customer-sharing/pipelines/${pipelineId}`, {
        mode: data.mode, targets: data.targets, bot_context: data.bot_context, expected_version: data.version,
      });
      if (active.current) { setData(response.data.data); setDirty(false); }
    } catch (e) { if (active.current) setError(message(e)); }
    finally { if (active.current) setBusy(false); }
  };
  const apply = async () => {
    setBusy(true); setError('');
    let after = 0;
    const count = { shared: 0, skipped: 0, failed: 0, issues: [], complete: false };
    setSummary({ ...count });
    try {
      let more = true;
      while (active.current && more) {
        const { data: response } = await axios.post(`/customer-sharing/pipelines/${pipelineId}/apply`, {
          after_id: after, max_id: data.max_id, expected_version: data.version,
        });
        const result = response.data;
        result.results.forEach(row => {
          count[row.status]++;
          if (row.status !== 'shared') count.issues.push(row);
        });
        if (result.has_more && result.after_id <= after) throw new Error('Sharing did not advance.');
        after = result.after_id; more = result.has_more;
        count.complete = !more;
        if (active.current) setSummary({ ...count, issues: [...count.issues] });
      }
    } catch (e) { if (active.current) setError(message(e)); }
    finally { if (active.current) setBusy(false); }
  };
  return <section className="customer-share pipeline-sharing" aria-label="Pipeline customer sharing">
    <header><ArrowRightLeft size={20} aria-hidden="true" /><div><h3>Connected customer profiles</h3><p>Choose how this pipeline shares with Auto Appy.</p></div></header>
    {error && <p role="alert">{error} <button type="button" disabled={busy} onClick={() => refresh(v => v + 1)}>Reload</button></p>}
    {!data && !error && <p role="status">Loading pipeline sharing…</p>}
    {data && <>
      <fieldset disabled={busy || !data.can_manage}>
        <legend>Sharing rule</legend>
        {[
          ['inherit', 'Use workspace default', 'Follow the organisation’s choice for new leads.'],
          ['manual', 'Only when I share', 'Keep new leads local until someone shares their profile.'],
          ['always', 'Always share', 'Connect new leads in this pipeline automatically.'],
        ].map(([mode, title, help]) => <label className={`pipeline-sharing-option ${data.mode === mode ? 'selected' : ''}`} key={mode}>
          <input type="radio" name={`pipeline-sharing-${pipelineId}`} checked={data.mode === mode} onChange={() => change({ mode })} />
          <span><strong>{title}</strong><small>{help}</small></span>
        </label>)}
        {data.mode !== 'manual' && <div className="pipeline-sharing-destinations">
          <label><input type="checkbox" checked={data.targets.includes('whatsapp')} onChange={e => change({ targets: e.target.checked ? ['whatsapp'] : [] })} /> Also connect to WhatsApp</label>
          <label><input type="checkbox" checked={data.bot_context} onChange={e => change({ bot_context: e.target.checked })} /> Allow the connected bot to use known CRM details</label>
          <small>Mail and Meetings use the same customer profile. Existing app permissions still apply.</small>
        </div>}
      </fieldset>
      <button type="button" disabled={busy || !dirty || !data.can_manage} onClick={save} className="customer-share-primary">{busy ? 'Working…' : 'Save pipeline rule'}</button>
      <div className="pipeline-sharing-existing">
        <strong>Existing leads · {data.total}</strong>
        <p>Saving the rule does not change existing leads. Apply it below to connect this pipeline’s current profiles. Individual stop and copy choices are retained.</p>
        <button type="button" disabled={busy || dirty || data.mode !== 'always' || !data.can_manage || !data.total} onClick={apply}><RefreshCw size={15} /> Apply to existing leads</button>
        {summary && <div role="status"><p><Check size={15} /> {summary.complete ? 'Finished' : 'Progress'}: {summary.shared} shared · {summary.skipped} skipped · {summary.failed} failed</p>
          {summary.issues.length > 0 && <details><summary>Review skipped or failed leads</summary><ul>{summary.issues.map(row => <li key={row.id}>Lead {row.id}: {row.reason}</li>)}</ul></details>}
        </div>}
      </div>
    </>}
  </section>;
}
